// biome-ignore-all lint/a11y/noNoninteractiveTabindex: the scrollable update log must be focusable to scroll with the keyboard
import { ArrowsClockwise, CloudArrowDown, DownloadSimple } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  type SystemView,
  systemQuery,
  type UpdaterJob,
  type UpdaterResult,
  type UpdaterView,
  type UpdateStatus,
  updaterQuery,
} from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { QueryBoundary } from "@/components/admin/shared";
import { MarkdownView } from "@/components/markdown/MarkdownView";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  CopyField,
  RelativeTime,
  RowSkeleton,
  SectionHeader,
  Spinner,
} from "@/components/ui";
import { ApiError, api } from "@/lib/api";

export const Route = createFileRoute("/_app/admin/system")({ component: SystemPage });

const POLL_MS = 3000;
const NOT_SET_UP: UpdaterView = { configured: false };
const runningJob = (u: UpdaterView | undefined) => (u?.configured && u.reachable ? u.running : null);

/**
 * This build's version, the update check against Cyrellian's update server and, with the optional updater sidecar,
 * one-click installs (otherwise the update.sh command).
 */
function SystemPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_system() }]);
  const system = useQuery(systemQuery);
  const qc = useQueryClient();
  const check = useMutation({
    mutationFn: () => api.post<SystemView>("/api/v1/admin/system/check"),
    onSuccess: (data) => qc.setQueryData(systemQuery.queryKey, data),
  });

  // While an update runs (started here or found running) the sidecar's status is polled. The app restarts in between,
  // so failing polls are expected; once the job is over the page reloads its data (new version, final result).
  const [started, setStarted] = useState(false);
  const polling = started || !!runningJob(system.data?.updater);
  const poll = useQuery({ ...updaterQuery, enabled: polling, refetchInterval: POLL_MS, retry: false });
  const live = (polling && poll.data) || system.data?.updater || NOT_SET_UP;
  const restarting = polling && poll.isError;
  const job = runningJob(live);

  useEffect(() => {
    if (!polling || !poll.data || poll.isError || job) return;
    setStarted(false);
    void qc.invalidateQueries({ queryKey: systemQuery.queryKey, exact: true });
  }, [polling, poll.data, poll.isError, job, qc]);

  return (
    <Page title={m.adm_nav_system()} icon={CloudArrowDown} meta={m.adm_sys_meta()} width="full" className="max-w-3xl">
      <QueryBoundary query={system} skeleton={<RowSkeleton />} needs="instance.admin">
        {({ updates: u }) => (
          <div className="flex flex-col gap-8">
            <section aria-labelledby="sys-version" className="flex flex-col gap-4">
              <SectionHeader id="sys-version" title={m.adm_sys_version_title()} />
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-panel border border-line bg-surface p-5 shadow-[0_1px_2px_var(--shadow-soft)]">
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
                  <dt className="text-fg-muted">{m.adm_sys_current()}</dt>
                  <dd className="font-mono text-fg">{u.current}</dd>
                  <dt className="text-fg-muted">{m.adm_sys_channel()}</dt>
                  <dd className="text-fg">{u.channel}</dd>
                  <dt className="text-fg-muted">{m.adm_sys_last_check()}</dt>
                  <dd className="text-fg">
                    {u.checkedAt ? <RelativeTime value={u.checkedAt} /> : m.adm_sys_never_checked()}
                  </dd>
                </dl>
                {u.enabled ? (
                  <Button onPress={() => check.mutate()} isPending={check.isPending}>
                    <ArrowsClockwise aria-hidden size={16} />
                    {m.adm_sys_check()}
                  </Button>
                ) : null}
              </div>
              {!u.enabled ? (
                <Callout tone="info" title={m.adm_sys_off_title()}>
                  {m.adm_sys_off_body()}
                </Callout>
              ) : u.error ? (
                <Callout tone="warning">{m.adm_sys_error({ error: u.error })}</Callout>
              ) : u.checkedAt && !u.updateAvailable ? (
                <Callout tone="success">{m.adm_sys_uptodate()}</Callout>
              ) : null}
            </section>

            {u.updateAvailable && u.latest ? (
              <Available
                u={u}
                updater={live}
                running={!!job || started}
                onStarted={(updater) => {
                  qc.setQueryData(updaterQuery.queryKey, updater);
                  setStarted(true);
                }}
              />
            ) : null}

            <UpdaterSection updater={live} restarting={restarting} />
          </div>
        )}
      </QueryBoundary>
    </Page>
  );
}

function updateError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case "update_running":
        return m.adm_sys_err_running();
      case "unknown_release":
        return m.adm_sys_err_unknown_release();
      case "manual_steps":
        return m.adm_sys_err_manual();
      case "not_offered":
      case "not_newer":
        return m.adm_sys_err_not_offered();
      case "updater_unreachable":
        return m.adm_sys_err_unreachable();
      case "updater_not_configured":
        return m.adm_sys_err_not_configured();
    }
  }
  return adminError(e);
}

function Available({
  u,
  updater,
  running,
  onStarted,
}: {
  u: UpdateStatus;
  updater: UpdaterView;
  running: boolean;
  onStarted: (updater: UpdaterView) => void;
}) {
  const latest = u.latest!;
  const releases = u.changelog.length ? u.changelog : [latest];
  const [confirm, setConfirm] = useState(false);
  // Releases that need manual steps are never installed with one click: the command stays the way.
  const oneClick = updater.configured && updater.reachable && !u.manual;

  return (
    <>
      <Callout tone={u.critical ? "danger" : "info"} title={m.adm_sys_available_title({ version: latest.version })}>
        {u.critical ? <p>{m.adm_sys_critical()}</p> : null}
        {u.manual ? <p>{m.adm_sys_manual()}</p> : null}
      </Callout>

      {oneClick ? (
        <section aria-labelledby="sys-install" className="flex flex-col gap-3">
          <SectionHeader id="sys-install" title={m.adm_sys_install_title()} description={m.adm_sys_install_body()} />
          <div>
            <Button variant="primary" onPress={() => setConfirm(true)} isDisabled={running}>
              {running ? <Spinner /> : <DownloadSimple aria-hidden size={16} />}
              {running ? m.adm_sys_install_running() : m.adm_sys_install({ version: latest.version })}
            </Button>
          </div>
          <ConfirmDialog
            isOpen={confirm}
            onOpenChange={setConfirm}
            title={m.adm_sys_install_confirm_title({ version: latest.version })}
            description={m.adm_sys_install_confirm_body()}
            confirmLabel={m.adm_sys_install_confirm()}
            tone="primary"
            describeError={updateError}
            onConfirm={async () => {
              const res = await api.post<{ updater: UpdaterView }>("/api/v1/admin/system/update", {
                version: latest.version,
              });
              onStarted(res.updater);
            }}
          />
        </section>
      ) : (
        <section aria-labelledby="sys-how" className="flex flex-col gap-3">
          <SectionHeader
            id="sys-how"
            title={m.adm_sys_how_title()}
            description={u.manual ? m.adm_sys_manual_install() : m.adm_sys_how_body()}
          />
          <CopyField label={m.adm_sys_how_label()} value={`./deploy/update.sh v${latest.version}`} />
        </section>
      )}

      <section aria-labelledby="sys-changes" className="flex flex-col gap-4">
        <SectionHeader id="sys-changes" title={m.adm_sys_changes_title()} />
        {releases.map((r) => (
          <article key={r.version} className="flex flex-col gap-2 border-l-2 border-line pl-4">
            <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-fg">
              <span className="font-mono">{r.version}</span>
              {r.critical ? <Badge tone="danger">{m.adm_sys_critical_badge()}</Badge> : null}
              {r.releasedAt ? <RelativeTime value={r.releasedAt} className="font-normal text-fg-muted" /> : null}
            </h3>
            {r.notes ? <MarkdownView source={r.notes} size="sm" headingOffset={3} /> : null}
            {r.notesUrl ? (
              <a href={r.notesUrl} target="_blank" rel="noreferrer" className="text-sm text-accent underline">
                {m.adm_sys_release_notes()}
              </a>
            ) : null}
          </article>
        ))}
      </section>
    </>
  );
}

const STEPS: Record<string, () => string> = {
  check: () => m.adm_sys_step_check(),
  fetch: () => m.adm_sys_step_fetch(),
  build: () => m.adm_sys_step_build(),
  backup: () => m.adm_sys_step_backup(),
  restart: () => m.adm_sys_step_restart(),
  health: () => m.adm_sys_step_health(),
  updater: () => m.adm_sys_step_updater(),
  rollback: () => m.adm_sys_step_rollback(),
};
const stepLabel = (step: string) => STEPS[step]?.() ?? step;

function Running({ job, restarting }: { job: UpdaterJob; restarting: boolean }) {
  return (
    <Callout tone="info" role="status" title={m.adm_sys_updater_running({ tag: job.tag })}>
      <p className="flex items-center gap-2">
        <Spinner />
        {restarting ? m.adm_sys_restarting() : stepLabel(job.step)}
      </p>
    </Callout>
  );
}

function LastResult({ last }: { last: UpdaterResult }) {
  if (last.status === "applied") {
    return (
      <Callout tone="success" role="status">
        {m.adm_sys_last_applied({ tag: last.tag })} (<RelativeTime value={last.at} />)
      </Callout>
    );
  }
  return (
    <Callout
      tone="danger"
      role="status"
      title={
        last.status === "rolled-back"
          ? m.adm_sys_last_rolled_back({ tag: last.tag })
          : m.adm_sys_last_failed({ tag: last.tag })
      }
    >
      {last.step ? <p>{m.adm_sys_last_failed_step({ step: stepLabel(last.step) })}</p> : null}
      {last.error ? <p className="font-mono text-xs break-words">{last.error}</p> : null}
      <p>
        <RelativeTime value={last.at} />
      </p>
    </Callout>
  );
}

function UpdaterSection({ updater, restarting }: { updater: UpdaterView; restarting: boolean }) {
  return (
    <section aria-labelledby="sys-updater" className="flex flex-col gap-3">
      <SectionHeader id="sys-updater" title={m.adm_sys_updater_title()} />
      {!updater.configured ? (
        <p className="text-sm text-pretty text-fg-muted">{m.adm_sys_updater_off()}</p>
      ) : !updater.reachable ? (
        <Callout tone="warning">{m.adm_sys_updater_unreachable({ error: updater.error })}</Callout>
      ) : (
        <>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
            <dt className="text-fg-muted">{m.adm_sys_updater_status()}</dt>
            <dd className="text-fg">
              {updater.running ? m.adm_sys_updater_running({ tag: updater.running.tag }) : m.adm_sys_updater_ready()}
            </dd>
            <dt className="text-fg-muted">{m.adm_sys_updater_checkout()}</dt>
            <dd className="font-mono text-fg">{updater.current.ref || "-"}</dd>
            {updater.running ? (
              <>
                <dt className="text-fg-muted">{m.adm_sys_updater_since()}</dt>
                <dd className="text-fg">
                  <RelativeTime value={updater.running.startedAt} />
                </dd>
              </>
            ) : null}
          </dl>
          {updater.error ? (
            <p className="text-sm text-fg-muted">{m.adm_sys_updater_git_error({ error: updater.error })}</p>
          ) : null}
          {updater.running ? <Running job={updater.running} restarting={restarting} /> : null}
          {!updater.running && updater.last ? <LastResult last={updater.last} /> : null}
          {updater.log ? (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-fg">{m.adm_sys_updater_log()}</h3>
              <section
                tabIndex={0}
                aria-label={m.adm_sys_updater_log()}
                className="scroll-thin max-h-80 overflow-auto rounded-control border border-line bg-subtle p-3 outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
              >
                <pre className="font-mono text-xs leading-5 whitespace-pre-wrap text-fg">{updater.log}</pre>
              </section>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
